import React from "react";

/**
 * Verifies if a react node is a valid `ReactElement` constructed from the supplied Class or Function Component.
 * This implies that it is NOT a
 * - `undefined`
 * - `null`
 * - `ReactText` (`string | number`)
 * - `boolean`
 * - `DOMElement`
 * - `ReactFragment`
 * - `ReactPortal`
 * @param reactNode A react node to verify.
 * @param component A component to assert.
 * @returns `true` if the react node is a valid `ReactElement` constructed from the supplied `component`.
 */
export function isValidComponentElement<P>(
  reactNode: React.ReactNode,
  component: React.ComponentType<P>
): reactNode is React.ReactElement<P, React.ComponentType<P>> {
  return React.isValidElement<P>(reactNode) && reactNode.type === component;
}

const CONTEXT_NAME_PATTERN = /^(\w+)State(?:Setter)?Context$/;
function getContextProviderNameFromContext(context: React.Context<any>) {
  const contextDisplayName = context.displayName;

  if (!contextDisplayName) {
    return "<Unknown Context Provider>";
  }

  const matchedContextDisplayName = contextDisplayName.match(CONTEXT_NAME_PATTERN);
  if (matchedContextDisplayName) {
    return `${matchedContextDisplayName[1]}ContextProvider`;
  }

  return contextDisplayName;
}

/**
 *
 * @param context The context to use.
 * @param suppressErrorMessage Suppress the "context provider missing" console error message.
 * @param contextConsumerHookName A user friendly name for the context consumer hook, to be used in the error message.
 * @returns
 */
export function useContextSafely<T>(
  context: React.Context<T>,
  suppressErrorMessage: boolean = false,
  contextConsumerHookName: string = "<Unknown Hook>"
): T {
  const contextValue = React.useContext(context);
  if (process.env.NODE_ENV !== "production") {
    if (!suppressErrorMessage && contextValue === undefined && typeof console !== "undefined") {
      console.error(
        `BDS: Context consumer hook ${contextConsumerHookName} could not find a context value. Make sure it is used within a properly initialized ${getContextProviderNameFromContext(
          context
        )}.`
      );
    }
  }
  return contextValue;
}
